import { useAuth } from '../../context/AuthContext';
import { useAccounts } from '../../context/AccountContext';
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from 'react-router-dom';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Link } from '@uhg-abyss/web/ui/Link';
import { styled } from '@uhg-abyss/web/tools/styled';
import SelectedAccountInfoBox from '../SelectedAccountInfoBox';
import { HelpMeAlert } from '../HelpMeAlert';
import { LockStatusResult } from '../../types/models';
import { useViewport } from '../../hooks/useViewport';

const buttonWidth = '19rem';

const BlueButton = styled(Button, {
    '&.abyss-button-root': {
        width: buttonWidth,
        backgroundColor: '$interactiveNav',
        borderColor: '$interactiveNav',
        color: '$textOnDark',
        '&:hover': {
            backgroundColor: '$surfaceHeroHover',
            borderColor: '$surfaceHeroHover',
            color: '$textPrimary',
            boxShadow: '1px 1px 1px #dadbdc',
        },
        '&:disabled': {
            backgroundColor: '$surfaceDisabled',
            borderColor: '$surfaceDisabled',
            color: '$textSecondary',
            boxShadow: '1px 1px 1px #dadbdc',
        },
        '&:focus': { outline: '2px solid $interactiveNav' },
    },
});

const WhiteButton = styled(Button, {
    '&.abyss-button-root': {
        width: buttonWidth,
        backgroundColor: '$surfaceWhite',
        borderColor: '$interactiveNav',
        borderWidth: 'var(--abyss-borderWidths-button-borderWidth-all-container) 1px',
        borderStyle: 'solid',
        color: '$interactiveNav',
        '&:hover': {
            backgroundColor: '$surfaceHeroHover',
            borderColor: '$interactiveNav',
            color: '$interactiveNav',
            boxShadow: '1px 1px 1px #dadbdc',
        },
        '&:disabled': {
            backgroundColor: '$surfaceDisabled',
            borderColor: '$surfaceDisabled',
            color: '$textSecondary',
            boxShadow: '1px 1px 1px #dadbdc',
        },
    },
});
const SpacerLine = styled('div', {
    borderBottom: '1px solid var(--abyss-colors-cardBase-color-border-container)',
    width: '320px',
});
const MainBlock = styled('div', {
    margin: '0.25rem 0',
    display: 'grid',
    justifyItems: 'center',
    gap: '0.5rem',
});
const BoldText = styled('span', {
    fontWeight: 'bold',
});

export const Home = () => {
    const { isTabletOrMobile, isPortrait } = useViewport();

    const welcomeHeadingLevel = isTabletOrMobile
        ? isPortrait
            ? 5   //h5 heading
            : 3   //h3 heading
        : 1;  //default, 1 is an h1 heading

    const welcomeMsgWeight = isTabletOrMobile
        ? 'medium'
        : 'bold';

    const {
        isAuthError,
        verifiedIdEnabled,
        tapEnabled,
        requiresIdentityReauth,
        verifiedIdIssuance,
        logout,
    } = useAuth();
    const {
        displayName,
        selectedAccount,
        lockStatus,
    } = useAccounts();
    const { t, i18n } = useTranslation();

    const formattedExpiryDate = verifiedIdIssuance
        ? new Intl.DateTimeFormat(i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC',
        }).format(new Date(verifiedIdIssuance.expirationTime))
        : null;

    const domainName = selectedAccount?.resourceName ?? 'MS';
    const isSelectedAccountTapEnabled = selectedAccount?.isTapEnabled ?? false;

    if (isAuthError) {
        return null;
    }

    return (
        <Layout.Stack alignLayout="center">
            <div>
                <Heading level={welcomeHeadingLevel} color="$textPrimary" id="welcomeHeading" textAlign="center">{t('welcome', { fullName: displayName })}</Heading>
            </div>

            <MainBlock>
                <SelectedAccountInfoBox />

                <div>
                    <Text color="$textPrimary" data-testid="welcomeMsg" fontSize='md' fontWeight={welcomeMsgWeight}>{t('welcomeMsg')}</Text>
                </div>

                {/* Buttons */}
                {/* Set New ID Password Button */}
                <div>
                    <RouterLink to="/new-password" tabIndex={-1}>
                    <BlueButton
                        id="setNewMSIDPasswordBtn"
                        size="$lg"
                    >{t('setNewIdPassword', { domainName })}</BlueButton>
                    </RouterLink>
                </div>
                {/* Test ID Password Button */}
                <div>
                    <RouterLink to="/test-password" tabIndex={-1}>
                    <WhiteButton
                        id="testMSIDPasswordBtn"
                        size="$lg"
                        variant="outline"
                    >{t('testIdPassword', { domainName })}</WhiteButton>
                    </RouterLink>
                </div>
                {/* MFA and Verified ID Section */}
                {(tapEnabled || verifiedIdEnabled) && (<>
                    <SpacerLine />
                    <h6>{t('mfaHeader')}</h6>
                </>)}
                {requiresIdentityReauth && (
                    <HelpMeAlert status="info">
                        <Text color="$textPrimary" data-testid="identityReauthMsg">
                            {t('identityReauthMessagePrefix')}
                            <Link variant="underlined" onClick={() => logout()}>
                                {t('identityReauthMessageLinkText')}
                            </Link>
                            {t('identityReauthMessageSuffix')}
                        </Text>
                    </HelpMeAlert>
                )}
                {!requiresIdentityReauth && (<>
                    {tapEnabled && isSelectedAccountTapEnabled && (
                        <div>
                            <RouterLink to="/setup-mfa" tabIndex={-1}>
                                <BlueButton
                                    id="setupMfaBtn"
                                    size="$lg"
                                    isDisabled={lockStatus === LockStatusResult.Pending}
                                >
                                    {t('setupResetMfa')}
                                </BlueButton>
                            </RouterLink>
                        </div>
                    )}
                    {tapEnabled && !isSelectedAccountTapEnabled && (
                        <HelpMeAlert status="info" data-testid="tapNotAvailableMsg" title={t('noTapOnAccountTitle')}>
                            {t('tapNotAvailableMsg')}
                        </HelpMeAlert>
                    )}

                    {verifiedIdEnabled && (<>
                        {verifiedIdIssuance && formattedExpiryDate && (
                            <HelpMeAlert status="info" title={t('verifiedIdDetailsHeader')}>
                                <Text color="$textPrimary" data-testid="verifiedIdDetailsMsg">
                                    {t('verifiedIdDetailsMessage1')}<BoldText>{formattedExpiryDate}</BoldText>{t('verifiedIdDetailsMessage2')}
                                </Text>
                                <br />
                                <Text color="$textPrimary" data-testid="verifiedIdDetailsSubMsg">
                                    {t('verifiedIdDetailsMessage3')}
                                </Text>
                            </HelpMeAlert>
                        )}

                        <RouterLink to="/verified-id" tabIndex={-1}>
                            <WhiteButton
                                id="createVerifiedIdBtn"
                                size="$lg"
                                isDisabled={lockStatus === LockStatusResult.Pending}
                            >
                                {t('createVerifiedId')}
                            </WhiteButton>
                        </RouterLink>
                    </>)}
                </>)}
            </MainBlock>
        </Layout.Stack>
    );
}
